import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { CircularProgress } from "@mui/material";
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Check,
  CheckCircle,
  ChevronRight,
  Clock,
  Gift,
  Image,
  Info,
  MessageSquare,
  Package,
  Phone,
  Tag,
  Target,
  Truck,
} from "lucide-react";
import { validateStep as validateNewDealStep } from "./validateNewDealStep";

const resolveImageSrc = (image) => {
  if (!image) return null;
  if (typeof image === "string") return image;
  if (image instanceof File || image instanceof Blob) {
    return URL.createObjectURL(image);
  }
  return null;
};

const ReviewRow = ({ label, value, mono = false }) => (
  <div className="flex gap-3 justify-between items-start py-2.5 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500 shrink-0">{label}</span>
    <span
      className={`text-sm font-medium text-gray-900 text-right max-w-[65%] break-words ${
        mono ? "font-mono" : ""
      }`}
    >
      {value || "—"}
    </span>
  </div>
);

const SectionCard = ({ icon: Icon, title, accent = "text-[#FF6C6C]", children }) => (
  <div className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
    <div className="flex gap-2.5 items-center px-5 py-4 border-b border-gray-100 bg-gray-50/80">
      <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-white border border-gray-200 shadow-sm">
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <h3 className="text-sm font-bold text-gray-900">{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const ReviewStep = ({
  formData,
  startDate,
  endDate,
  redemptionLocationType,
  selectedBranches,
  redemptionLocationTypeStep4,
  selectAllRedemptionLocations,
  selectedRedemptionBranches,
  selectAllLocations,
  selectAllFreeDeliveryLocations,
  selectedPartners,
  primaryImagePreview,
  imagePreview,
  isSubmitting,
  isPriceValid,
  onPrevStep,
  onGoToStep,
  onSubmit,
  variant = "create",
}) => {
  const isEdit = variant === "edit";
  const copy = isEdit
    ? {
        title: "Review & update",
        subtitle:
          "Confirm your changes before saving. Updated deals may go through review again.",
        issuesAction: "before updating",
        ready: "Ready to update",
        notReady: "Complete required fields to update",
        submitting: "Updating…",
        submit: "Update deal",
      }
    : {
        title: "Review & publish",
        subtitle:
          "Confirm everything looks correct. Your deal goes to review before it goes live.",
        issuesAction: "before publishing",
        ready: "Ready to publish",
        notReady: "Complete required fields to publish",
        submitting: "Publishing…",
        submit: "Publish deal",
      };
  const validateContext = {
    formData,
    startDate,
    endDate,
    redemptionLocationType,
    selectedBranches,
    redemptionLocationTypeStep4,
    selectAllRedemptionLocations,
    selectedRedemptionBranches,
  };

  const readinessIssues = useMemo(() => {
    const issues = [];

    [1, 2, 3, 4].forEach((step) => {
      const errors = validateNewDealStep(step, validateContext);
      Object.values(errors).forEach((message) => {
        issues.push({ step, message });
      });
    });

    const heroImage =
      primaryImagePreview ||
      resolveImageSrc(formData.image) ||
      imagePreview ||
      resolveImageSrc(formData.images?.[0]);

    if (!heroImage) {
      issues.push({
        step: 2,
        message: "Primary image is required before publishing",
      });
    }

    return issues;
  }, [
    formData,
    startDate,
    endDate,
    redemptionLocationType,
    selectedBranches,
    redemptionLocationTypeStep4,
    selectAllRedemptionLocations,
    selectedRedemptionBranches,
    primaryImagePreview,
    imagePreview,
  ]);

  const heroImage =
    primaryImagePreview ||
    resolveImageSrc(formData.image) ||
    imagePreview ||
    resolveImageSrc(formData.images?.[0]);

  const galleryImages = useMemo(() => {
    const fromForm = (formData.images || [])
      .map(resolveImageSrc)
      .filter(Boolean);
    if (fromForm.length > 0) return fromForm;
    return heroImage ? [heroImage] : [];
  }, [formData.images, heroImage]);

  const pricing = useMemo(() => {
    if (formData.offer_type === "discount") {
      const original = parseFloat(formData.discount_original_price) || 0;
      const sale = parseFloat(formData.discount_sale_price) || 0;
      const savings = Math.max(original - sale, 0);
      const pct = original > 0 ? Math.round((savings / original) * 100) : 0;
      return {
        headline: `GH₵ ${sale.toFixed(2)}`,
        subline: original > 0 ? `was GH₵ ${original.toFixed(2)}` : null,
        badge: pct > 0 ? `${pct}% off` : null,
        savings: savings > 0 ? `Save GH₵ ${savings.toFixed(2)}` : null,
      };
    }
    if (formData.offer_type === "bundle") {
      return {
        headline: `GH₵ ${formData.bundle_price || "0.00"}`,
        subline: "Bundle price",
        badge: "Bundle",
        savings: formData.bundle_savings
          ? `Save GH₵ ${formData.bundle_savings}`
          : null,
      };
    }
    if (formData.offer_type === "bogo") {
      return {
        headline: `GH₵ ${formData.bogo_item_price || "0.00"}`,
        subline: `${formData.bogo_quantity || 1} + ${formData.bogo_free_quantity || 1} BOGO`,
        badge: "BOGO",
        savings: null,
      };
    }
    return {
      headline: `GH₵ ${formData.price || "0.00"}`,
      subline: "Standard price",
      badge: null,
      savings: null,
    };
  }, [formData]);

  const durationDays = useMemo(() => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24));
  }, [startDate, endDate]);

  const targetSummary = useMemo(() => {
    if (selectAllLocations) return "Nationwide";
    if (redemptionLocationType === "branch" && selectedBranches.length) {
      return `${selectedBranches.length} branch(es)`;
    }
    if (redemptionLocationType === "partner" && selectedPartners.length) {
      return `${selectedPartners.length} partner(s)`;
    }
    const regions = (formData.region_location || []).filter(
      (loc) => loc.region && loc.location
    );
    if (regions.length === 1 && regions[0].region === "all") return "Nationwide";
    if (regions.length) return `${regions.length} location(s)`;
    return "Not set";
  }, [
    selectAllLocations,
    redemptionLocationType,
    selectedBranches,
    selectedPartners,
    formData.region_location,
  ]);

  const redemptionSummary = useMemo(() => {
    const types = formData.redemption_type?.length
      ? formData.redemption_type.join(" · ")
      : "Not set";
    if (!formData.redemption_type?.includes("In-store")) {
      return types;
    }
    if (selectAllRedemptionLocations) {
      return `${types} · Nationwide in-store`;
    }
    if (redemptionLocationTypeStep4 === "branch") {
      return `${types} · ${selectedRedemptionBranches.length} branch(es)`;
    }
    return types;
  }, [
    formData.redemption_type,
    selectAllRedemptionLocations,
    redemptionLocationTypeStep4,
    selectedRedemptionBranches,
  ]);

  const canPublish =
    readinessIssues.length === 0 && isPriceValid && !isSubmitting;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="overflow-hidden relative p-6 sm:p-8 bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="absolute top-0 right-0 w-40 h-40 bg-[#FF6C6C]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative">
          <p className="text-xs font-semibold tracking-wide text-[#FF6C6C] uppercase">
            Final step
          </p>
          <h2 className="mt-1 text-2xl font-bold text-gray-900">
            {copy.title}
          </h2>
          <p className="mt-2 max-w-xl text-sm text-gray-500">
            {copy.subtitle}
          </p>
        </div>
      </div>

      {/* Issues */}
      {readinessIssues.length > 0 && (
        <div className="p-5 bg-amber-50 rounded-2xl border border-amber-200">
          <div className="flex gap-2 items-center mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="text-sm font-bold text-amber-900">
              {readinessIssues.length} issue{readinessIssues.length > 1 ? "s" : ""} to fix {copy.issuesAction}
            </h3>
          </div>
          <ul className="space-y-2">
            {readinessIssues.slice(0, 6).map((issue, index) => (
              <li
                key={`${issue.step}-${index}`}
                className="flex gap-3 justify-between items-center px-3 py-2 bg-white rounded-xl border border-amber-100"
              >
                <span className="text-sm text-gray-700">{issue.message}</span>
                <button
                  type="button"
                  onClick={() => onGoToStep(issue.step)}
                  className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-[#FF6C6C] hover:underline"
                >
                  Fix
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Hero preview */}
      <div className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr]">
          <div className="relative bg-gray-50 min-h-[220px] lg:min-h-full flex items-center justify-center">
            {heroImage ? (
              <img
                src={heroImage}
                alt={formData.title || "Deal preview"}
                className="object-contain w-full h-full max-h-[320px] lg:max-h-[360px] min-h-[220px] p-3"
              />
            ) : (
              <div className="flex flex-col justify-center items-center h-full min-h-[220px] p-6 text-center">
                <Image className="w-10 h-10 text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-500">No primary image</p>
                <button
                  type="button"
                  onClick={() => onGoToStep(2)}
                  className="mt-2 text-xs font-semibold text-[#FF6C6C] hover:underline"
                >
                  Upload on step 2
                </button>
              </div>
            )}
            {pricing.badge && (
              <span className="absolute top-4 left-4 px-3 py-1 text-xs font-bold text-white bg-[#FF6C6C] rounded-full shadow-md">
                {pricing.badge}
              </span>
            )}
          </div>

          <div className="p-6 sm:p-8">
            <div className="flex flex-wrap gap-2 mb-3">
              {(formData.category || []).slice(0, 3).map((cat) => (
                <span
                  key={cat}
                  className="px-2.5 py-1 text-xs font-medium text-[#FF6C6C] bg-[#FF6C6C]/10 rounded-full"
                >
                  {cat}
                </span>
              ))}
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug">
              {formData.title || "Untitled deal"}
            </h3>
            {formData.product_name && (
              <p className="mt-1 text-sm text-gray-500">{formData.product_name}</p>
            )}

            <div className="flex flex-wrap gap-4 items-end mt-5">
              <div>
                <p className="text-3xl font-extrabold text-gray-900">{pricing.headline}</p>
                {pricing.subline && (
                  <p className="text-sm text-gray-400 line-through">{pricing.subline}</p>
                )}
              </div>
              {pricing.savings && (
                <span className="px-3 py-1 text-sm font-semibold text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-100">
                  {pricing.savings}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-3 mt-6 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-gray-400" />
                {formData.validity_type === "flash_sale"
                  ? `Flash · ${formData.flash_duration || 0}h`
                  : `${startDate || "—"} → ${endDate || "—"}`}
              </span>
              {durationDays != null && formData.validity_type !== "flash_sale" && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {durationDays} days
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 capitalize">
                <Tag className="w-4 h-4 text-gray-400" />
                {formData.offer_type || "standard"} offer
              </span>
            </div>
          </div>
        </div>

        {galleryImages.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 sm:px-8">
            <p className="mb-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">
              Gallery · {galleryImages.length} image{galleryImages.length !== 1 ? "s" : ""}
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {galleryImages.map((src, index) => (
                <div
                  key={`${src}-${index}`}
                  className="flex overflow-hidden shrink-0 justify-center items-center w-16 h-16 rounded-xl border-2 border-white bg-gray-50 shadow-sm sm:w-20 sm:h-20"
                >
                  <img src={src} alt={`Gallery ${index + 1}`} className="object-contain w-full h-full p-1" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Detail grid */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionCard icon={Package} title="Product & offer">
          <ReviewRow label="Product category" value={formData.productCategory} />
          <ReviewRow label="Subcategory" value={formData.product_subcategory} />
          {formData.productCategory === "Fashion & Accessories" &&
            formData.sizes?.length > 0 && (
              <ReviewRow
                label="Sizes"
                value={formData.sizes
                  .map((row) =>
                    typeof row === "string"
                      ? row
                      : `${row.size} (${row.quantity ?? 0})`
                  )
                  .join(" · ")}
              />
            )}
          <ReviewRow label="SKU" value={formData.sku} mono />
          <ReviewRow
            label="Gender"
            value={
              formData.gender?.includes("All Genders")
                ? "All Genders"
                : formData.gender?.join(", ")
            }
          />
          <ReviewRow
            label="Quantity"
            value={
              formData.quantity === 0 || formData.quantity == null
                ? "Unlimited"
                : String(formData.quantity)
            }
          />
          {formData.properties?.length > 0 && (
            <ReviewRow
              label="Properties"
              value={formData.properties
                .map((p) => `${p.name}: ${p.value}`)
                .join(" · ")}
            />
          )}
        </SectionCard>

        <SectionCard icon={Phone} title="Contact & SMS" accent="text-blue-600">
          <ReviewRow
            label="Phone"
            value={formData.phone_number ? `233${formData.phone_number}` : null}
          />
          <ReviewRow
            label="SMS"
            value={
              formData.sms_content
                ? `"${formData.sms_content.slice(0, 80)}${formData.sms_content.length > 80 ? "…" : ""}"`
                : null
            }
          />
        </SectionCard>

        <SectionCard icon={Target} title="Targeting" accent="text-purple-600">
          <ReviewRow label="Audience reach" value={targetSummary} />
          <ReviewRow
            label="Location mode"
            value={
              redemptionLocationType === "region"
                ? "Region"
                : redemptionLocationType === "branch"
                ? "Branch"
                : "Partner"
            }
          />
        </SectionCard>

        <SectionCard icon={Gift} title="Redemption" accent="text-indigo-600">
          <ReviewRow label="Methods" value={redemptionSummary} />
          {formData.redemption_type?.includes("Online") && (
            <ReviewRow label="Online link" value={formData.online_link} />
          )}
          {formData.redemption_instructions && (
            <ReviewRow label="Instructions" value={formData.redemption_instructions} />
          )}
        </SectionCard>

        <SectionCard icon={Truck} title="Delivery">
          <ReviewRow
            label="Delivery fee"
            value={
              formData.delivery_fee === "not_apply"
                ? "No delivery"
                : formData.delivery_fee === "apply"
                ? "Standard fees apply"
                : formData.delivery_fee === "free_within_location"
                ? selectAllFreeDeliveryLocations
                  ? "Free nationwide"
                  : "Free in selected areas"
                : "Not specified"
            }
          />
        </SectionCard>
      </div>

      {/* Description — full width row */}
      <div className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="flex gap-2.5 items-center px-5 py-4 border-b border-gray-100 bg-gray-50/80">
          <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-white border border-gray-200 shadow-sm">
            <MessageSquare className="w-4 h-4 text-teal-600" />
          </div>
          <h3 className="text-sm font-bold text-gray-900">Description</h3>
        </div>
        <div className="p-5">
          {formData.description?.trim() ? (
            <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
              {formData.description}
            </p>
          ) : (
            <p className="text-sm text-gray-400 italic">No description provided</p>
          )}
        </div>
      </div>

      {/* Terms */}
      {formData.terms_and_conditions && (
        <details className="group bg-white rounded-2xl border border-gray-200 shadow-sm">
          <summary className="flex gap-2 items-center px-5 py-4 cursor-pointer list-none font-semibold text-gray-900 hover:bg-gray-50 rounded-2xl">
            <FileTextIcon />
            Terms & conditions
            <ChevronRight className="ml-auto w-4 h-4 text-gray-400 transition-transform group-open:rotate-90" />
          </summary>
          <div className="px-5 pb-5 max-h-64 overflow-y-auto text-sm leading-relaxed text-gray-600 whitespace-pre-wrap border-t border-gray-100 pt-4">
            {formData.terms_and_conditions}
          </div>
        </details>
      )}

      {/* Publish card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm sm:p-6"
      >
        <div className="flex justify-center mb-4">
          <div
            className={`inline-flex gap-2 items-center px-4 py-2 rounded-full border text-sm font-semibold ${
              canPublish
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-gray-100 text-gray-600 border-gray-200"
            }`}
          >
            {canPublish ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <AlertTriangle className="w-4 h-4" />
            )}
            {canPublish ? copy.ready : copy.notReady}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={onPrevStep}
            className="inline-flex gap-2 justify-center items-center px-5 py-3 w-full text-sm font-medium text-gray-700 whitespace-nowrap bg-white rounded-xl border-2 border-gray-300 transition-colors hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            Back
          </button>

          <button
            type="button"
            onClick={onSubmit}
            disabled={!canPublish}
            className={`inline-flex gap-2 justify-center items-center px-5 py-3 w-full text-sm font-semibold whitespace-nowrap rounded-xl transition-all ${
              canPublish
                ? "bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white shadow-lg shadow-[#FF6C6C]/25 hover:shadow-xl"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={18} color="inherit" />
                {copy.submitting}
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 shrink-0" />
                {copy.submit}
              </>
            )}
          </button>
        </div>

        <p className="flex gap-2 justify-center items-center pt-4 mt-4 text-xs text-center text-gray-500 border-t border-gray-100">
          <Info className="w-3.5 h-3.5 shrink-0" />
          Your deal will be reviewed by our team before going live
        </p>
      </motion.div>
    </div>
  );
};

const FileTextIcon = () => (
  <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default ReviewStep;
